import { useState } from 'react';
import { Title } from 'react-admin';
import { useQuery, useMutation } from '@apollo/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Slider from '@mui/material/Slider';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';

import { GET_HYBRID_LIMITS, UPDATE_HYBRID_LIMIT } from './payment.queries';
import { formatAmount, TYPE_LABELS } from './payment.types';
import { CURRENCY_LABEL } from '@ekaa/tokens/vocabulary';

interface HybridLimit {
  id: string;
  paymentType: string;
  maxEkaaPercentage: number;
  maxEkaaAmount: number;
  minCashAmount: number;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

interface EditDialogProps {
  limit: HybridLimit | null;
  open: boolean;
  onClose: () => void;
  onSave: (paymentType: string, input: any) => void;
  loading: boolean;
}

function EditLimitDialog({ limit, open, onClose, onSave, loading }: EditDialogProps) {
  const [maxPercentage, setMaxPercentage] = useState(limit?.maxEkaaPercentage ?? 50);
  const [maxAmount, setMaxAmount] = useState(limit?.maxEkaaAmount ?? 50000);
  const [minCash, setMinCash] = useState(limit?.minCashAmount ?? 100);
  const [isEnabled, setIsEnabled] = useState(limit?.isEnabled ?? true);

  // Reset form when limit changes
  if (limit && maxPercentage !== limit.maxEkaaPercentage && !open) {
    setMaxPercentage(limit.maxEkaaPercentage);
    setMaxAmount(limit.maxEkaaAmount);
    setMinCash(limit.minCashAmount);
    setIsEnabled(limit.isEnabled);
  }

  const handleSave = () => {
    if (!limit) return;
    onSave(limit.paymentType, {
      maxEkaaPercentage: maxPercentage,
      maxEkaaAmount: maxAmount,
      minCashAmount: minCash,
      isEnabled,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Modifier les limites hybrides
        {limit && (
          <Typography variant="body2" color="text.secondary">
            Type: {TYPE_LABELS[limit.paymentType as keyof typeof TYPE_LABELS] || limit.paymentType}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Enable toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography>Paiement hybride activé</Typography>
            <Switch checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />
          </Box>

          {/* Max percentage slider */}
          <Box>
            <Typography gutterBottom>
              Pourcentage maximum {CURRENCY_LABEL}: <strong>{maxPercentage}%</strong>
            </Typography>
            <Slider
              value={maxPercentage}
              onChange={(_, value) => setMaxPercentage(value as number)}
              min={0}
              max={100}
              step={5}
              marks={[
                { value: 0, label: '0%' },
                { value: 25, label: '25%' },
                { value: 50, label: '50%' },
                { value: 75, label: '75%' },
                { value: 100, label: '100%' },
              ]}
              valueLabelDisplay="auto"
              sx={{
                color: '#D4AF37',
                '& .MuiSlider-thumb': {
                  backgroundColor: '#D4AF37',
                },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Part maximale du montant total payable en {CURRENCY_LABEL}
            </Typography>
          </Box>

          {/* Max EKAA amount */}
          <TextField
            label={`Montant maximum ${CURRENCY_LABEL}`}
            type="number"
            value={maxAmount}
            onChange={(e) => setMaxAmount(Number(e.target.value))}
            InputProps={{
              endAdornment: <InputAdornment position="end">{CURRENCY_LABEL}</InputAdornment>,
            }}
            helperText={`Montant maximum utilisable en ${CURRENCY_LABEL} par transaction`}
            fullWidth
          />

          {/* Min cash amount */}
          <TextField
            label="Montant minimum en cash"
            type="number"
            value={minCash}
            onChange={(e) => setMinCash(Number(e.target.value))}
            InputProps={{
              endAdornment: <InputAdornment position="end">FCFA</InputAdornment>,
            }}
            helperText="Montant minimum obligatoire à payer en espèces/mobile money"
            fullWidth
          />

          {/* Preview */}
          <Card sx={{ backgroundColor: '#f5f5f5' }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Aperçu pour un paiement de 10 000 FCFA
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Max {CURRENCY_LABEL}
                  </Typography>
                  <Typography fontWeight={600} color="#D4AF37">
                    {Math.min((10000 * maxPercentage) / 100, maxAmount).toLocaleString('fr-FR')} {CURRENCY_LABEL}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Min Cash
                  </Typography>
                  <Typography fontWeight={600}>
                    {Math.max(10000 - Math.min((10000 * maxPercentage) / 100, maxAmount), minCash).toLocaleString(
                      'fr-FR'
                    )}{' '}
                    FCFA
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * HybridLimitsEditor Component
 * Allows admins to configure hybrid payment limits per payment type
 */
export function HybridLimitsEditor() {
  const [editingLimit, setEditingLimit] = useState<HybridLimit | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data, loading, refetch } = useQuery<{ hybridPaymentLimits: HybridLimit[] }>(GET_HYBRID_LIMITS);

  const [updateLimit, { loading: updateLoading }] = useMutation(UPDATE_HYBRID_LIMIT, {
    onCompleted: () => {
      setSnackbar({ open: true, message: 'Limites mises à jour avec succès', severity: 'success' });
      setEditingLimit(null);
      refetch();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: `Erreur: ${error.message}`, severity: 'error' });
    },
  });

  const limits = data?.hybridPaymentLimits || [];

  const handleSave = (paymentType: string, input: any) => {
    updateLimit({
      variables: {
        paymentType,
        input,
      },
    });
  };

  return (
    <Box>
      <Title title="Limites de paiement hybride" />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Configuration des limites hybrides
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Définissez les limites d'utilisation des {CURRENCY_LABEL} par type de paiement
          </Typography>
        </Box>
        <Tooltip title="Actualiser">
          <IconButton onClick={() => refetch()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Info card */}
      <Card sx={{ mb: 3, backgroundColor: '#FFF8E1' }}>
        <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <InfoIcon color="warning" />
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              À propos des paiements hybrides
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Les paiements hybrides permettent aux utilisateurs de combiner leurs {CURRENCY_LABEL} (crédits de fidélité) avec un
              paiement en espèces ou mobile money. Vous pouvez définir les limites par type de transaction pour
              contrôler l'utilisation des {CURRENCY_LABEL}.
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type de paiement</TableCell>
                <TableCell align="center">Statut</TableCell>
                <TableCell align="right">Max {CURRENCY_LABEL} (%)</TableCell>
                <TableCell align="right">Max {CURRENCY_LABEL} (montant)</TableCell>
                <TableCell align="right">Min Cash</TableCell>
                <TableCell align="right">Dernière modification</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={60} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="circular" width={32} height={32} />
                    </TableCell>
                  </TableRow>
                ))
              ) : limits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Aucune limite configurée</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                limits.map((limit) => (
                  <TableRow key={limit.id} hover>
                    <TableCell>
                      <Typography fontWeight={500}>
                        {TYPE_LABELS[limit.paymentType as keyof typeof TYPE_LABELS] || limit.paymentType}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={limit.isEnabled ? 'Activé' : 'Désactivé'}
                        size="small"
                        color={limit.isEnabled ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={600} color="#D4AF37">
                        {limit.maxEkaaPercentage}%
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatAmount(limit.maxEkaaAmount, 'EKAA')}</TableCell>
                    <TableCell align="right">{formatAmount(limit.minCashAmount, 'XOF')}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {new Date(limit.updatedAt).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Modifier">
                        <IconButton size="small" onClick={() => setEditingLimit(limit)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Edit dialog */}
      <EditLimitDialog
        limit={editingLimit}
        open={Boolean(editingLimit)}
        onClose={() => setEditingLimit(null)}
        onSave={handleSave}
        loading={updateLoading}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default HybridLimitsEditor;
